import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getWatcherSettings, saveWatcherSettings, sendWatcherTestEmail, sendWatcherTestSyslog } from '../../redux/actions/configActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import TextInput from '../../components/TextInput';
import SelectBox from '../../components/SelectBox';

import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';

import WatcherSettingStyles from './WatcherSettings.module.css';
import Button from '../../components/Button';
import BackupSettings from './BackupSettings';
import DigestSettings from './DigestSettings';
import SessionSettings from './SessionSettings';

const FRONTEND_WATCHER_SETTINGS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	SAVE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	TEST_EMAIL_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildWatcherSettingsSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const isEnabled = (value) => {
	if (typeof value === 'boolean') return value;
	if (typeof value === 'number') return value === 1;
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		return normalized === '1' || normalized === 'true' || normalized === 'enabled';
	}
	return false;
};

const displayPreviewValue = (value) => {
	if (value === null || value === undefined) return 'N/A';
	const text = String(value).trim();
	return text === '' ? 'N/A' : text;
};

const TEST_NOTIFICATION_OPTIONS = [
	{ value: 'watcher_traffic_alert', label: 'Watcher Traffic Alert' },
	{ value: 'watcher_new_device_alert', label: 'Watcher New Device Alert' },
	{ value: 'risky_signin_unable_reset_password', label: 'Risky Sign-in Notification - Unable to Reset Password' },
	{ value: 'risky_signin_successful_password_reset', label: 'Risky Sign-in Notification - Successful Password Reset' },
	{ value: 'user_account_creation', label: 'User Account Creation' },
	{ value: 'siem_alert', label: 'SIEM Alert' },
	{ value: 'edr_alert', label: 'EDR Alert' },
];

const getNotificationLabel = (value) =>
	TEST_NOTIFICATION_OPTIONS.find((option) => option.value === value)?.label || TEST_NOTIFICATION_OPTIONS[0].label;

const WatcherSettingsScreen = () => {
	const dispatch = useDispatch();
	const userLogin = useSelector((state) => state.userLogin);

	const [ userRole, setUserRole ] = React.useState('');
	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ watcherSettingsData, setWatcherSettingsData ] = React.useState({});
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);
	const [ actionToast, setActionToast ] = React.useState({ type: '', message: '' });
	const [ testEmailPreview, setTestEmailPreview ] = React.useState(null);
	const actionToastTimerRef = React.useRef(null);

	const [ sysLogServer, setSysLogServer ] = React.useState({ value: '', enabled: false, port: '514', protocol: 'udp' });
	// Mail transport: 'graph' (Microsoft 365) or 'smtp'. Both configs are kept so
	// switching back does not lose settings; only the active one sends.
	const [ mailProvider, setMailProvider ] = React.useState('graph');
	const [ graph, setGraph ] = React.useState({
		enabled: false, tenantId: '', clientId: '', clientSecret: '',
		sender: '', recipient: '', secretSet: false,
	});
	const [ smtp, setSmtp ] = React.useState({
		host: '', port: '', user: '', password: '', sender: '', security: 'starttls', passwordSet: false,
	});
	// Master switch for outbound notification email + the per-feature gate for
	// immediate critical domain-change alerts (both default off).
	const [ emailEnabled, setEmailEnabled ] = React.useState(false);
	const [ emailFormat, setEmailFormat ] = React.useState('html');
	const [ domainAlertCritical, setDomainAlertCritical ] = React.useState(false);
	// Portal-wide MFA switch. Default on (secure); turned off for air-gapped
	// sites where authenticator clock drift keeps locking admins out.
	const [ requireMfa, setRequireMfa ] = React.useState(true);
	// Air-gapped deployment: username-only users (temp password, no invite emails)
	// and alerts sent to the fixed Recipient(s) below instead of per-user emails.
	const [ airgapMode, setAirgapMode ] = React.useState(false);
	// Public URL of this portal, used to build absolute links in outbound email.
	const [ portalUrl, setPortalUrl ] = React.useState('');
	const [ testNotificationType, setTestNotificationType ] = React.useState(TEST_NOTIFICATION_OPTIONS[0].value);

	const setWatcherSettingsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildWatcherSettingsSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearWatcherSettingsSupportError = React.useCallback(() => {
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const clearActionToast = React.useCallback(() => {
		setActionToast({ type: '', message: '' });
	}, []);

	const clearTestEmailPreview = React.useCallback(() => {
		setTestEmailPreview(null);
	}, []);

	const showActionToast = React.useCallback((type, message, timeoutMs = 4000) => {
		if (actionToastTimerRef.current) {
			clearTimeout(actionToastTimerRef.current);
			actionToastTimerRef.current = null;
		}

		setActionToast({ type, message });

		actionToastTimerRef.current = setTimeout(() => {
			setActionToast({ type: '', message: '' });
			actionToastTimerRef.current = null;
		}, timeoutMs);
	}, []);

	React.useEffect(() => {
		return () => {
			if (actionToastTimerRef.current) {
				clearTimeout(actionToastTimerRef.current);
			}
		};
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			if (
				userLogin &&
				userLogin.userInfo &&
				userLogin.userInfo.status === true &&
				userLogin.userInfo.session &&
				userLogin.userInfo.session.role
			) {
				setUserRole(userLogin.userInfo.session.role);
			}
		},
		[ userLogin ]
	);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearWatcherSettingsSupportError();
				try {
					const data = await dispatch(getWatcherSettings());
					if (data && data.status) {
						setWatcherSettingsData(data);
						clearWatcherSettingsSupportError();
					} else {
						setWatcherSettingsData({});
						setWatcherSettingsSupportError(
							FRONTEND_WATCHER_SETTINGS_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Watcher Settings Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Watcher settings request did not return success')
						);
					}
				} catch (error) {
					setWatcherSettingsData({});
					setWatcherSettingsSupportError(
						FRONTEND_WATCHER_SETTINGS_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Watcher Settings Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ dispatch, clearWatcherSettingsSupportError, setWatcherSettingsSupportError ]
	);

	React.useEffect(
		() => {
			if (watcherSettingsData && watcherSettingsData.status === true && !watcherSettingsData.msg) {
				setSysLogServer({
					value: watcherSettingsData.SyslogServer || '',
					enabled: isEnabled(watcherSettingsData.SyslogEnable),
					port: watcherSettingsData.SyslogPort || '514',
					protocol: (watcherSettingsData.SyslogProtocol === 'tcp' ? 'tcp' : 'udp'),
				});

				setMailProvider(watcherSettingsData.MailProvider === 'smtp' ? 'smtp' : 'graph');
				setPortalUrl(watcherSettingsData.PortalUrl || '');
				setEmailEnabled(isEnabled(watcherSettingsData.EmailEnabled));
				setEmailFormat(watcherSettingsData.EmailFormat === 'text' ? 'text' : 'html');
				setDomainAlertCritical(isEnabled(watcherSettingsData.DomainAlertEmailCritical));
				// Absent => secure default (on). Only an explicit false turns it off.
				setRequireMfa(watcherSettingsData.RequireMfa !== false);
				setAirgapMode(isEnabled(watcherSettingsData.AirgapMode));
				setGraph({
					enabled: isEnabled(watcherSettingsData.GraphEnable),
					tenantId: watcherSettingsData.GraphTenantId || '',
					clientId: watcherSettingsData.GraphClientId || '',
					clientSecret: '',
					sender: watcherSettingsData.GraphSender || '',
					recipient: watcherSettingsData.GraphRecipient || '',
					secretSet: !!watcherSettingsData.GraphClientSecretSet,
				});
				setSmtp({
					host: watcherSettingsData.SmtpHost || '',
					port: watcherSettingsData.SmtpPort || '',
					user: watcherSettingsData.SmtpUser || '',
					password: '',
					sender: watcherSettingsData.SmtpSender || '',
					security: watcherSettingsData.SmtpSecurity || 'starttls',
					passwordSet: !!watcherSettingsData.SmtpPasswordSet,
				});
			}
		},
		[ watcherSettingsData ]
	);

	const buildSettingsFormData = React.useCallback(() => {
		const formData = new FormData();
		formData.append('btn', 'Save');
		formData.append('SyslogEnable', sysLogServer.enabled);
		formData.append('SyslogServer', sysLogServer.value);
		formData.append('SyslogPort', sysLogServer.port);
		formData.append('SyslogProtocol', sysLogServer.protocol);
		formData.append('MailProvider', mailProvider);
		formData.append('PortalUrl', portalUrl);
		formData.append('EmailEnabled', emailEnabled);
		formData.append('EmailFormat', emailFormat);
		formData.append('DomainAlertEmailCritical', domainAlertCritical);
		formData.append('RequireMfa', requireMfa);
		formData.append('AirgapMode', airgapMode);
		formData.append('GraphEnable', graph.enabled);
		formData.append('GraphTenantId', graph.tenantId);
		formData.append('GraphClientId', graph.clientId);
		// Blank secret/password means "leave the stored one unchanged".
		formData.append('GraphClientSecret', graph.clientSecret);
		formData.append('GraphSender', graph.sender);
		formData.append('GraphRecipient', graph.recipient);
		formData.append('SmtpHost', smtp.host);
		formData.append('SmtpPort', smtp.port);
		formData.append('SmtpUser', smtp.user);
		formData.append('SmtpPassword', smtp.password);
		formData.append('SmtpSender', smtp.sender);
		formData.append('SmtpSecurity', smtp.security);
		return formData;
	}, [ sysLogServer, graph, mailProvider, smtp, emailEnabled, domainAlertCritical, portalUrl, requireMfa, airgapMode ]);

	const persistSettings = React.useCallback(async () => {
		const formData = buildSettingsFormData();
		try {
			const data = await dispatch(saveWatcherSettings(formData));
			if (data && data.status) {
				clearWatcherSettingsSupportError();
				return { status: true, data };
			}

			setWatcherSettingsSupportError(
				FRONTEND_WATCHER_SETTINGS_ERROR_CODES.SAVE_FAILED,
				'Watcher Settings Page / Save Settings Failed',
				data?.msg || (typeof data === 'string' ? data : 'Save watcher settings request did not return success')
			);
			return { status: false, data };
		} catch (error) {
			setWatcherSettingsSupportError(
				FRONTEND_WATCHER_SETTINGS_ERROR_CODES.SAVE_FAILED,
				'Watcher Settings Page / Save Settings Request Error',
				error?.message || String(error)
			);
			return { status: false };
		}
	}, [ dispatch, buildSettingsFormData, clearWatcherSettingsSupportError, setWatcherSettingsSupportError ]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (userRole !== 'admin') return;
		clearActionToast();
		clearTestEmailPreview();

		setDataLoading(true);
		const saveResult = await persistSettings();
		setDataLoading(false);

		if (saveResult.status) {
			showActionToast('success', saveResult.data?.msg || 'Settings saved successfully.');
		} else {
			showActionToast('error', 'Unable to save settings. Review error details above.');
		}
	};

	const handleTestEmail = async (e) => {
		e.preventDefault();
		if (userRole !== 'admin') return;
		clearActionToast();
		clearTestEmailPreview();

		setDataLoading(true);
		const saveResult = await persistSettings();
		if (!saveResult.status) {
			showActionToast('error', 'Settings were not saved, so test email was not sent.');
			setDataLoading(false);
			return;
		}

		try {
			const selectedNotificationLabel = getNotificationLabel(testNotificationType);
			const data = await dispatch(sendWatcherTestEmail({ notificationType: testNotificationType }));
			if (data && data.status) {
				clearWatcherSettingsSupportError();
				showActionToast('success', data.msg || 'Test email sent successfully.');
				setTestEmailPreview(
					data.emailPreview || {
						from: mailProvider === 'smtp' ? smtp.sender : graph.sender,
						to: graph.recipient,
						subject: selectedNotificationLabel,
						body: `Notification template test email for "${selectedNotificationLabel}".`,
						notificationType: testNotificationType,
						notificationLabel: selectedNotificationLabel,
					}
				);
			} else {
				const failureMessage =
					data?.msg || (typeof data === 'string' ? data : 'Test email request did not return success');
				setWatcherSettingsSupportError(
					FRONTEND_WATCHER_SETTINGS_ERROR_CODES.TEST_EMAIL_FAILED,
					'Watcher Settings Page / Test Email Failed',
					failureMessage
				);
				showActionToast('error', 'Unable to send test email. Review support error details above.');
			}
		} catch (error) {
			setWatcherSettingsSupportError(
				FRONTEND_WATCHER_SETTINGS_ERROR_CODES.TEST_EMAIL_FAILED,
				'Watcher Settings Page / Test Email Request Error',
				error?.message || String(error)
			);
			showActionToast('error', 'Unable to send test email. Review support error details above.');
		} finally {
			setDataLoading(false);
		}
	};

	const handleTestSyslog = async () => {
		if (!sysLogServer.value) {
			showActionToast('error', 'Set the Syslog Server first.');
			return;
		}
		setDataLoading(true);
		try {
			const data = await dispatch(sendWatcherTestSyslog());
			if (data && data.status) {
				clearWatcherSettingsSupportError();
				showActionToast('success', data.msg || 'Test syslog sent.');
			} else {
				const failureMessage =
					data?.msg || (typeof data === 'string' ? data : 'Test syslog request did not return success');
				setWatcherSettingsSupportError(
					FRONTEND_WATCHER_SETTINGS_ERROR_CODES.TEST_EMAIL_FAILED,
					'Watcher Settings Page / Test Syslog Failed',
					failureMessage
				);
				// Show the real reason (e.g. "Cannot connect … connection refused")
				// in the toast, not a generic message — this is a connectivity test.
				showActionToast('error', failureMessage);
			}
		} catch (error) {
			setWatcherSettingsSupportError(
				FRONTEND_WATCHER_SETTINGS_ERROR_CODES.TEST_EMAIL_FAILED,
				'Watcher Settings Page / Test Syslog Request Error',
				error?.message || String(error)
			);
			showActionToast('error', 'Unable to send test syslog. Review support error details above.');
		} finally {
			setDataLoading(false);
		}
	};

	return (
		<Content currentMenu='configuration' headerTitle='Watcher Settings'>
			{dataLoading ? (
				<Loader />
			) : watcherSettingsData && Object.keys(watcherSettingsData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={WatcherSettingStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={WatcherSettingStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={WatcherSettingStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</div>
			) : (
				<form onSubmit={handleSubmit}>
					{supportErrorMessage && (
						<div className={WatcherSettingStyles.supportErrorBanner}>
							<div className={WatcherSettingStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={WatcherSettingStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={WatcherSettingStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}

					<div className={WatcherSettingStyles.content}>
						<TextInput
							icon='server'
							label='Syslog Server'
							placeholder='Syslog Server'
							switchBox={true}
							value={sysLogServer.value}
							onChange={(e) => setSysLogServer({ ...sysLogServer, value: e.target.value })}
							disabled={!sysLogServer.enabled || userRole !== 'admin'}
							switchChecked={sysLogServer.enabled}
							switchOnChange={() => setSysLogServer({ ...sysLogServer, enabled: !sysLogServer.enabled })}
						/>
					</div>

					<div className={WatcherSettingStyles.content}>
						<TextInput
							icon='server'
							label='Syslog Port'
							placeholder='514'
							value={sysLogServer.port}
							onChange={(e) => setSysLogServer({ ...sysLogServer, port: e.target.value })}
							disabled={!sysLogServer.enabled || userRole !== 'admin'}
						/>
						<SelectBox
							label='Syslog Protocol'
							name='syslog-protocol'
							value={sysLogServer.protocol}
							onChange={(e) => setSysLogServer({ ...sysLogServer, protocol: e.target.value })}
							disabled={!sysLogServer.enabled || userRole !== 'admin'}
						>
							<option value='udp'>UDP</option>
							<option value='tcp'>TCP</option>
						</SelectBox>
					</div>

					<div className={WatcherSettingStyles.content}>
						<Button
							label='Test Syslog'
							onClick={handleTestSyslog}
							disabled={!sysLogServer.enabled || userRole !== 'admin'}
						/>
					</div>

					<div className={WatcherSettingStyles.content}>
						<SelectBox
							label='Email Provider'
							name='mail-provider'
							value={mailProvider}
							onChange={(e) => setMailProvider(e.target.value)}
							disabled={userRole !== 'admin'}
						>
							<option value='graph'>Microsoft 365 (Graph)</option>
							<option value='smtp'>SMTP</option>
						</SelectBox>
						<SelectBox
							label='Email Notifications'
							name='email-enabled'
							value={emailEnabled ? 'on' : 'off'}
							onChange={(e) => setEmailEnabled(e.target.value === 'on')}
							disabled={userRole !== 'admin'}
						>
							<option value='off'>Disabled</option>
							<option value='on'>Enabled</option>
						</SelectBox>
						<SelectBox
							label='Email Format'
							name='email-format'
							value={emailFormat}
							onChange={(e) => setEmailFormat(e.target.value)}
							disabled={userRole !== 'admin' || !emailEnabled}
						>
							<option value='html'>HTML (styled)</option>
							<option value='text'>Plain text (most compatible)</option>
						</SelectBox>
						<SelectBox
							label='Critical Domain-Change Alerts'
							name='domain-alert-critical'
							value={domainAlertCritical ? 'on' : 'off'}
							onChange={(e) => setDomainAlertCritical(e.target.value === 'on')}
							disabled={userRole !== 'admin' || !emailEnabled}
						>
							<option value='off'>Disabled</option>
							<option value='on'>Enabled</option>
						</SelectBox>
						<SelectBox
							label='Require MFA (Two-Factor)'
							name='require-mfa'
							value={requireMfa ? 'on' : 'off'}
							onChange={(e) => setRequireMfa(e.target.value === 'on')}
							disabled={userRole !== 'admin'}
						>
							<option value='on'>Required</option>
							<option value='off'>Disabled (password only)</option>
						</SelectBox>
						<SelectBox
							label='Air-Gap Mode'
							name='airgap-mode'
							value={airgapMode ? 'on' : 'off'}
							onChange={(e) => setAirgapMode(e.target.value === 'on')}
							disabled={userRole !== 'admin'}
						>
							<option value='off'>Off (users use email invites)</option>
							<option value='on'>On (username-only + fixed recipient)</option>
						</SelectBox>
					</div>

					<div className={WatcherSettingStyles.content}>
						<TextInput
							icon='home'
							label='Portal URL (for links in email)'
							placeholder='https://c2c.yourdomain.com'
							value={portalUrl}
							onChange={(e) => setPortalUrl(e.target.value)}
							disabled={userRole !== 'admin'}
						/>
					</div>

					{mailProvider === 'graph' && (
						<>
							<div className={WatcherSettingStyles.content}>
								<TextInput
									icon='server'
									label='O365 Graph Email (Sender mailbox)'
									placeholder='alerts@yourdomain.com'
									switchBox={true}
									value={graph.sender}
									onChange={(e) => setGraph({ ...graph, sender: e.target.value })}
									disabled={!graph.enabled || userRole !== 'admin'}
									switchChecked={graph.enabled}
									switchOnChange={() => setGraph({ ...graph, enabled: !graph.enabled })}
								/>
							</div>
							<div className={WatcherSettingStyles.content}>
								<TextInput
									icon='server'
									label='Tenant ID'
									placeholder='Directory (tenant) ID'
									disabled={userRole !== 'admin'}
									value={graph.tenantId}
									onChange={(e) => setGraph({ ...graph, tenantId: e.target.value })}
								/>
								<TextInput
									icon='server'
									label='Client ID'
									placeholder='Application (client) ID'
									disabled={userRole !== 'admin'}
									value={graph.clientId}
									onChange={(e) => setGraph({ ...graph, clientId: e.target.value })}
								/>
								<TextInput
									icon='server'
									type='password'
									label={graph.secretSet ? 'Client Secret (leave blank to keep current)' : 'Client Secret'}
									placeholder='Client secret value'
									disabled={userRole !== 'admin'}
									value={graph.clientSecret}
									onChange={(e) => setGraph({ ...graph, clientSecret: e.target.value })}
								/>
							</div>
						</>
					)}

					{mailProvider === 'smtp' && (
						<div className={WatcherSettingStyles.content}>
							<TextInput
								icon='server'
								label='SMTP Server'
								placeholder='smtp.yourdomain.com'
								disabled={userRole !== 'admin'}
								value={smtp.host}
								onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
							/>
							<TextInput
								icon='server'
								label='Port'
								placeholder='587'
								disabled={userRole !== 'admin'}
								value={smtp.port}
								onChange={(e) => setSmtp({ ...smtp, port: e.target.value })}
							/>
							<SelectBox
								label='Security'
								name='smtp-security'
								value={smtp.security}
								onChange={(e) => setSmtp({ ...smtp, security: e.target.value })}
								disabled={userRole !== 'admin'}
							>
								<option value='starttls'>STARTTLS (usually 587)</option>
								<option value='ssl'>SSL / TLS (usually 465)</option>
								<option value='none'>None (plaintext relay)</option>
							</SelectBox>
							<TextInput
								icon='server'
								label='Username'
								placeholder='Leave blank for an open relay'
								disabled={userRole !== 'admin'}
								value={smtp.user}
								onChange={(e) => setSmtp({ ...smtp, user: e.target.value })}
							/>
							<TextInput
								icon='server'
								type='password'
								label={smtp.passwordSet ? 'Password (leave blank to keep current)' : 'Password'}
								placeholder='SMTP password'
								disabled={userRole !== 'admin'}
								value={smtp.password}
								onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
							/>
							<TextInput
								icon='server'
								label='From Address'
								placeholder='alerts@yourdomain.com'
								disabled={userRole !== 'admin'}
								value={smtp.sender}
								onChange={(e) => setSmtp({ ...smtp, sender: e.target.value })}
							/>
						</div>
					)}

					<div className={WatcherSettingStyles.content}>
						<TextInput
							icon='server'
							label='Recipient(s)'
							placeholder='alerts@x.com, soc@y.com — comma-separated for several'
							disabled={userRole !== 'admin'}
							value={graph.recipient}
							onChange={(e) => setGraph({ ...graph, recipient: e.target.value })}
						/>
					</div>

					{userRole === 'admin' && (
						<>
							<div className={WatcherSettingStyles.content}>
								<SelectBox
									label='Notification Test Type'
									name='notification-test-type'
									value={testNotificationType}
									onChange={(e) => setTestNotificationType(e.target.value)}
								>
									{TEST_NOTIFICATION_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</SelectBox>
							</div>
							<div className={WatcherSettingStyles.actionReminder}>
								Reminder: Save your settings before testing email so the test uses your latest values for the selected provider.
							</div>
							<div className={WatcherSettingStyles.actionsRow}>
								<Button label='Save Settings' style={{ marginTop: '2rem' }} onClick={handleSubmit} />
								<Button
									label='Test Email'
									style={{ marginTop: '2rem' }}
									variant='secondary'
									onClick={handleTestEmail}
								/>
							</div>
								{actionToast.message && (
									<div
										className={`${WatcherSettingStyles.actionToast} ${
											actionToast.type === 'success'
												? WatcherSettingStyles.actionToastSuccess
												: WatcherSettingStyles.actionToastError
										}`}
										data-testid='settings-action-toast'
									>
										{actionToast.message}
									</div>
								)}
							{testEmailPreview && (
								<div className={WatcherSettingStyles.testEmailPreview} data-testid='test-email-preview'>
									<div className={WatcherSettingStyles.testEmailPreviewTitle}>Sent Email Preview</div>
									<div className={WatcherSettingStyles.testEmailPreviewRow}>
										<span className={WatcherSettingStyles.testEmailPreviewLabel}>Notification:</span>
										<span>
											{displayPreviewValue(
												testEmailPreview.notificationLabel ||
													getNotificationLabel(testEmailPreview.notificationType)
											)}
										</span>
									</div>
									<div className={WatcherSettingStyles.testEmailPreviewRow}>
										<span className={WatcherSettingStyles.testEmailPreviewLabel}>From:</span>
										<span>{displayPreviewValue(testEmailPreview.from)}</span>
									</div>
									<div className={WatcherSettingStyles.testEmailPreviewRow}>
										<span className={WatcherSettingStyles.testEmailPreviewLabel}>To:</span>
										<span>{displayPreviewValue(testEmailPreview.to)}</span>
									</div>
									<div className={WatcherSettingStyles.testEmailPreviewRow}>
										<span className={WatcherSettingStyles.testEmailPreviewLabel}>Subject:</span>
										<span>{displayPreviewValue(testEmailPreview.subject)}</span>
									</div>
									<div className={WatcherSettingStyles.testEmailPreviewRow}>
										<span className={WatcherSettingStyles.testEmailPreviewLabel}>Sent At:</span>
										<span>{displayPreviewValue(testEmailPreview.sentAt)}</span>
									</div>
									<div className={WatcherSettingStyles.testEmailPreviewBody}>
										{displayPreviewValue(testEmailPreview.body)}
									</div>
								</div>
							)}
						</>
					)}
				</form>
			)}
			{/* Database backups — self-contained, provider-only (renders nothing
			    otherwise), and saved independently of the settings form above. */}
			<BackupSettings styles={WatcherSettingStyles} />
			{/* Daily digest — self-contained, provider-only (renders nothing
			    otherwise), saved independently of the settings form above. */}
			<DigestSettings styles={WatcherSettingStyles} />
			{/* Session / sign-out policy — provider-only, self-contained. */}
			<SessionSettings styles={WatcherSettingStyles} />
		</Content>
	);
};

export default WatcherSettingsScreen;
